import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, X, Pencil } from 'lucide-react';

interface Gym {
    id: string;
    name: string;
    displayName: string;
    messagingProvider: string;
}

interface WhatsappAccount {
    id: string;
    gymId: string | null;
    provider: string;
    phoneNumber: string;
    phoneNumberId: string | null;
    isShared: boolean;
    status: string;
    accessToken: string | null;
    credentials: Record<string, any> | null;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    const url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};
const API_BASE_URL = getApiBaseUrl();

const PROVIDERS = ['meta', 'whatchimp', 'aisensy', 'disabled'];
const ACCOUNT_PROVIDERS = ['meta', 'whatchimp', 'aisensy'];

const providerBadge: Record<string, string> = {
    meta: 'bg-blue-100 text-blue-700',
    whatchimp: 'bg-green-100 text-green-700',
    aisensy: 'bg-purple-100 text-purple-700',
    disabled: 'bg-gray-200 text-gray-600'
};

export default function PartnersTab({ token }: Props) {
    const [gyms, setGyms] = useState<Gym[]>([]);
    const [accounts, setAccounts] = useState<WhatsappAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showAccountForm, setShowAccountForm] = useState(false);
    const [accountForm, setAccountForm] = useState<any>({
        provider: 'whatchimp', gymId: '', phoneNumber: '', phoneNumberId: '',
        accessToken: '', credentialsJson: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [editingAccount, setEditingAccount] = useState<WhatsappAccount | null>(null);
    const [editForm, setEditForm] = useState<any>({
        provider: '', gymId: '', phoneNumber: '', phoneNumberId: '',
        accessToken: '', credentialsJson: '', status: 'active'
    });
    const [editSubmitting, setEditSubmitting] = useState(false);

    const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchData(); }, [token]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [gymRes, accRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/saas/gyms/list`, { headers: authHeaders }),
                fetch(`${API_BASE_URL}/api/saas/whatsapp-accounts`, { headers: authHeaders })
            ]);
            const gymData = await gymRes.json();
            const accData = await accRes.json();
            setGyms(gymData.data || []);
            setAccounts(accData.data || []);
        } catch {
            toast.error('Failed to load partners data');
        } finally {
            setLoading(false);
        }
    };

    const setGymProvider = async (gymId: string, provider: string) => {
        // optimistic
        setGyms(prev => prev.map(g => g.id === gymId ? { ...g, messagingProvider: provider } : g));
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/messaging-provider`, {
                method: 'PATCH', headers: authHeaders, body: JSON.stringify({ messagingProvider: provider })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
            toast.success('Messaging approach updated');
        } catch (e: any) {
            toast.error(e.message || 'Failed to update');
            fetchData();
        }
    };

    const submitAccount = async () => {
        if (!accountForm.provider || !accountForm.phoneNumber) {
            toast.error('Provider and phone number are required');
            return;
        }
        let credentials: any = undefined;
        if (accountForm.credentialsJson.trim()) {
            try { credentials = JSON.parse(accountForm.credentialsJson); }
            catch { toast.error('Credentials must be valid JSON'); return; }
        }
        try {
            setSubmitting(true);
            const res = await fetch(`${API_BASE_URL}/api/saas/whatsapp-accounts`, {
                method: 'POST', headers: authHeaders, body: JSON.stringify({
                    provider: accountForm.provider,
                    gymId: accountForm.gymId || null,
                    phoneNumber: accountForm.phoneNumber,
                    phoneNumberId: accountForm.phoneNumberId || null,
                    accessToken: accountForm.accessToken || null,
                    credentials
                    // isShared is derived server-side from gymId (no gym = shared).
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed');
            toast.success('Account onboarded');
            setShowAccountForm(false);
            setAccountForm({ provider: 'whatchimp', gymId: '', phoneNumber: '', phoneNumberId: '', accessToken: '', credentialsJson: '', isShared: false });
            fetchData();
        } catch (e: any) {
            toast.error(e.message || 'Failed');
        } finally {
            setSubmitting(false);
        }
    };

    const openEdit = (account: WhatsappAccount) => {
        setEditingAccount(account);
        setEditForm({
            provider: account.provider,
            gymId: account.gymId || '',
            phoneNumber: account.phoneNumber,
            phoneNumberId: account.phoneNumberId || '',
            accessToken: '',
            credentialsJson: '',
            status: account.status
        });
    };

    const submitEditAccount = async () => {
        if (!editingAccount) return;
        if (!editForm.provider || !editForm.phoneNumber) {
            toast.error('Provider and phone number are required');
            return;
        }
        let credentials: any = undefined;
        if (editForm.credentialsJson.trim()) {
            try { credentials = JSON.parse(editForm.credentialsJson); }
            catch { toast.error('Credentials must be valid JSON'); return; }
        }
        try {
            setEditSubmitting(true);
            const body: any = {
                provider: editForm.provider,
                gymId: editForm.gymId || null,
                phoneNumber: editForm.phoneNumber,
                phoneNumberId: editForm.phoneNumberId || null,
                status: editForm.status
            };
            // Only send secrets if the admin actually typed a new value —
            // GET returns them masked ('****'), so an untouched field must not overwrite the real value.
            if (editForm.accessToken.trim()) body.accessToken = editForm.accessToken;
            if (credentials !== undefined) body.credentials = credentials;

            const res = await fetch(`${API_BASE_URL}/api/saas/whatsapp-accounts/${editingAccount.id}`, {
                method: 'PUT', headers: authHeaders, body: JSON.stringify(body)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed');
            toast.success('Account updated');
            setEditingAccount(null);
            fetchData();
        } catch (e: any) {
            toast.error(e.message || 'Failed');
        } finally {
            setEditSubmitting(false);
        }
    };

    const gymName = (id: string | null) => {
        if (!id) return 'Shared';
        const g = gyms.find(x => x.id === id);
        return g ? (g.displayName || g.name) : id;
    };

    const filteredGyms = gyms.filter(g => (g.displayName || g.name || '').toLowerCase().includes(search.toLowerCase()));

    const credHint: Record<string, string> = {
        whatchimp: '{"apiToken":"...","phoneNumberId":"...","baseUrl":"https://app.whatchimp.com/api/v1"}',
        aisensy: '{"apiKey":"...","baseUrl":"https://api.aisensy.com/v1"}',
        meta: '(Meta uses env WHATSAPP_ACCESS_TOKEN / WHATSAPP_APP_SECRET — only Phone number ID is stored)'
    };

    return (
        <div className="space-y-8">
            {/* Partner accounts */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Partner Accounts</h3>
                        <p className="text-sm text-gray-500">Provider credentials used to send messages (Meta / WhatChimp / AiSensy).</p>
                    </div>
                    <button onClick={() => setShowAccountForm(true)} className="inline-flex items-center gap-1 bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-indigo-700">
                        <Plus size={16} /> Add Account
                    </button>
                </div>
                {loading ? <p className="text-sm text-gray-500">Loading…</p> : (
                    <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="text-left px-3 py-2">Provider</th>
                                    <th className="text-left px-3 py-2">Phone</th>
                                    <th className="text-left px-3 py-2">Assigned to</th>
                                    <th className="text-left px-3 py-2">Scope</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                    <th className="text-left px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {accounts.length === 0 ? (
                                    <tr><td colSpan={6} className="px-3 py-3 text-gray-500">No accounts yet.</td></tr>
                                ) : accounts.map(a => (
                                    <tr key={a.id}>
                                        <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs capitalize ${providerBadge[a.provider] || 'bg-gray-100'}`}>{a.provider}</span></td>
                                        <td className="px-3 py-2">{a.phoneNumber}</td>
                                        <td className="px-3 py-2">{gymName(a.gymId)}</td>
                                        <td className="px-3 py-2">{a.isShared ? 'Shared' : 'Dedicated'}</td>
                                        <td className="px-3 py-2 capitalize">{a.status}</td>
                                        <td className="px-3 py-2">
                                            <button onClick={() => openEdit(a)} aria-label={`Edit ${a.phoneNumber}`} className="text-gray-400 hover:text-indigo-600">
                                                <Pencil size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Per-gym messaging approach */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Messaging Approach per Gym</h3>
                        <p className="text-sm text-gray-500">Choose which provider each customer sends through. "Disabled" blocks all sends.</p>
                    </div>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search gyms…" className="border rounded-md px-3 py-1.5 text-sm" />
                </div>
                {loading ? <p className="text-sm text-gray-500">Loading…</p> : (
                    <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="text-left px-3 py-2">Gym</th>
                                    <th className="text-left px-3 py-2">Current approach</th>
                                    <th className="text-left px-3 py-2">Change</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {filteredGyms.map(g => (
                                    <tr key={g.id}>
                                        <td className="px-3 py-2 font-medium text-gray-900">{g.displayName || g.name}</td>
                                        <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs capitalize ${providerBadge[g.messagingProvider] || 'bg-gray-100'}`}>{g.messagingProvider}</span></td>
                                        <td className="px-3 py-2">
                                            <select value={g.messagingProvider} onChange={e => setGymProvider(g.id, e.target.value)} className="border rounded-md px-2 py-1 text-sm capitalize">
                                                {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Add account modal */}
            {showAccountForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold">Add Provider Account</h4>
                            <button onClick={() => setShowAccountForm(false)}><X size={18} /></button>
                        </div>
                        <div className="space-y-3">
                            <label className="text-sm block">Provider
                                <select value={accountForm.provider} onChange={e => setAccountForm({ ...accountForm, provider: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    {ACCOUNT_PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </label>
                            <label className="text-sm block">Gym
                                <select value={accountForm.gymId} onChange={e => setAccountForm({ ...accountForm, gymId: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    <option value="">— Shared (all gyms) —</option>
                                    {gyms.map(g => <option key={g.id} value={g.id}>{g.displayName || g.name}</option>)}
                                </select>
                                <span className="text-xs text-gray-400">Leave as "Shared" for a platform-wide account, or pick a gym for a dedicated one.</span>
                            </label>
                            <label className="text-sm block">Phone number
                                <input value={accountForm.phoneNumber} onChange={e => setAccountForm({ ...accountForm, phoneNumber: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm block">Phone number ID
                                <input value={accountForm.phoneNumberId} onChange={e => setAccountForm({ ...accountForm, phoneNumberId: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            {accountForm.provider === 'meta' ? (
                                <div className="text-sm">
                                    <span>Access token / app secret</span>
                                    <p className="mt-1 text-xs text-gray-500 bg-gray-50 border rounded-md px-2 py-1.5">
                                        Managed via environment (<code>WHATSAPP_ACCESS_TOKEN</code> / <code>WHATSAPP_APP_SECRET</code>) — not stored in the database. Only the Phone number ID below is needed for Meta.
                                    </p>
                                </div>
                            ) : (
                                <label className="text-sm block">Access token / API key
                                    <input value={accountForm.accessToken} onChange={e => setAccountForm({ ...accountForm, accessToken: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                                </label>
                            )}
                            <label className="text-sm block">Credentials (JSON, optional)
                                <textarea value={accountForm.credentialsJson} onChange={e => setAccountForm({ ...accountForm, credentialsJson: e.target.value })} rows={3} placeholder={credHint[accountForm.provider]} className="mt-1 w-full border rounded-md px-2 py-1.5 font-mono text-xs" />
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setShowAccountForm(false)} className="px-3 py-1.5 text-sm border rounded-md">Cancel</button>
                            <button onClick={submitAccount} disabled={submitting} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md disabled:opacity-50">{submitting ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit account modal */}
            {editingAccount && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold">Edit Provider Account</h4>
                            <button onClick={() => setEditingAccount(null)}><X size={18} /></button>
                        </div>
                        <div className="space-y-3">
                            <label className="text-sm block">Provider
                                <select value={editForm.provider} onChange={e => setEditForm({ ...editForm, provider: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    {ACCOUNT_PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </label>
                            <label className="text-sm block">Gym
                                <select value={editForm.gymId} onChange={e => setEditForm({ ...editForm, gymId: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    <option value="">— Shared (all gyms) —</option>
                                    {gyms.map(g => <option key={g.id} value={g.id}>{g.displayName || g.name}</option>)}
                                </select>
                                <span className="text-xs text-gray-400">Leave as "Shared" for a platform-wide account, or pick a gym for a dedicated one.</span>
                            </label>
                            <label className="text-sm block">Phone number
                                <input value={editForm.phoneNumber} onChange={e => setEditForm({ ...editForm, phoneNumber: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm block">Phone number ID
                                <input value={editForm.phoneNumberId} onChange={e => setEditForm({ ...editForm, phoneNumberId: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm block">Status
                                <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    <option value="active">active</option>
                                    <option value="inactive">inactive</option>
                                </select>
                            </label>
                            {editForm.provider === 'meta' ? (
                                <div className="text-sm">
                                    <span>Access token / app secret</span>
                                    <p className="mt-1 text-xs text-gray-500 bg-gray-50 border rounded-md px-2 py-1.5">
                                        Managed via environment (<code>WHATSAPP_ACCESS_TOKEN</code> / <code>WHATSAPP_APP_SECRET</code>) — not stored in the database. Only the Phone number ID above is needed for Meta.
                                    </p>
                                </div>
                            ) : (
                                <label className="text-sm block">Access token / API key
                                    <input value={editForm.accessToken} onChange={e => setEditForm({ ...editForm, accessToken: e.target.value })} placeholder="•••• (leave blank to keep current)" className="mt-1 w-full border rounded-md px-2 py-1.5" />
                                </label>
                            )}
                            <label className="text-sm block">Credentials (JSON, optional)
                                <textarea value={editForm.credentialsJson} onChange={e => setEditForm({ ...editForm, credentialsJson: e.target.value })} rows={3} placeholder="Leave blank to keep existing credentials" className="mt-1 w-full border rounded-md px-2 py-1.5 font-mono text-xs" />
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setEditingAccount(null)} className="px-3 py-1.5 text-sm border rounded-md">Cancel</button>
                            <button onClick={submitEditAccount} disabled={editSubmitting} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md disabled:opacity-50">{editSubmitting ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
